// Chi Siamo + Metodo + Perché Epic Flow (fusi in una sola sezione editoriale spaziosa).
// Testi, immagini e benefit sovrascrivibili via /admin/landing-config.
import { useLandingConfig } from "@/hooks/useLandingConfig";
import SafeImg from "@/components/landing/SafeImg";
import { Check } from "lucide-react";

// Fallback bullets condivisi con LandingWhyChooseUs — così se il super_admin
// aggiorna `texts.method.bullets` (o `texts.why_choose.bullets`) dal CMS
// Landing, entrambe le sezioni restano coerenti.
const DEFAULT_METHOD_BULLETS = [
  "Classi a numero chiuso (max 2-8 persone) per seguire ogni movimento con cura",
  "Attrezzi professionali (Reformer & Cadillac)",
  "Massima flessibilità di prenotazione tramite APP",
  "Ambiente accogliente e specializzato, adatto anche a chi non ha mai fatto Pilates",
  "Percorsi mirati per gravidanza, senior e problematiche fisiche comuni",
];

export default function LandingMethod() {
  const { T, cfg } = useLandingConfig();
  const eyebrow = T("texts.method.eyebrow", "Chi siamo · Il nostro metodo");
  const t1 = T("texts.method.title_line1", "Non è un semplice Pilates.");
  const t2 = T("texts.method.title_line2", "È un metodo.");
  const italicWord = T("texts.method.title_italic_word", "");
  const subtitle = T("texts.method.subtitle", "Un percorso lento, consapevole, costruito insieme. Ogni sessione unisce tecnica, ascolto e personalizzazione, con un obiettivo preciso: farti stare meglio nel tuo corpo, ogni giorno.");
  const photo = T("images.method_photo", "");
  // Sotto-titolo "Consapevolezza, cura, trasformazione." con italic sulla parola
  // finale — riprende il claim della vecchia sezione "Perché Epic Flow"
  // (LandingWhyChooseUs) accorpata qui.
  const bulletsHeadingP1 = T("texts.method.bullets_heading_p1", "Consapevolezza,");
  const bulletsHeadingP2 = T("texts.method.bullets_heading_p2", "cura, trasformazione.");
  const bulletsHeadingItalic = T("texts.method.bullets_heading_italic", "trasformazione");
  // Priorità: bullets specifici del metodo → bullets di why_choose → default
  const cmsMethodBullets = Array.isArray(cfg?.texts?.method?.bullets) && cfg.texts.method.bullets.length > 0
    ? cfg.texts.method.bullets
    : (Array.isArray(cfg?.texts?.why_choose?.bullets) && cfg.texts.why_choose.bullets.length > 0
        ? cfg.texts.why_choose.bullets
        : DEFAULT_METHOD_BULLETS);

  const renderBulletsHeading = () => {
    const line = bulletsHeadingP2;
    if (!bulletsHeadingItalic || !line?.toLowerCase().includes(bulletsHeadingItalic.toLowerCase())) {
      return (<>{bulletsHeadingP1}<br />{line}</>);
    }
    const idx = line.toLowerCase().indexOf(bulletsHeadingItalic.toLowerCase());
    return (
      <>
        {bulletsHeadingP1}
        <br />
        {line.slice(0, idx)}
        <span className="font-editorial text-primary">{line.slice(idx, idx + bulletsHeadingItalic.length)}</span>
        {line.slice(idx + bulletsHeadingItalic.length)}
      </>
    );
  };

  const renderTitleLine = (line) => {
    if (!italicWord || !line?.toLowerCase().includes(italicWord.toLowerCase())) return line;
    const idx = line.toLowerCase().indexOf(italicWord.toLowerCase());
    return (
      <>
        {line.slice(0, idx)}
        <span className="font-editorial text-mauve">{line.slice(idx, idx + italicWord.length)}</span>
        {line.slice(idx + italicWord.length)}
      </>
    );
  };

  return (
    <section data-testid="landing-method" id="chisiamo" className="relative bg-plum-dark text-cream">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-32 md:py-40 text-center">
        <div className="text-[10px] uppercase tracking-[0.42em] text-cream/60 mb-10 flex items-center justify-center gap-3">
          <span className="w-10 h-px bg-cream/40" />
          {eyebrow}
          <span className="w-10 h-px bg-cream/40" />
        </div>

        <h2 className="font-serif-display font-light text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-cream">
          {renderTitleLine(t1)}
          <br />
          {italicWord ? renderTitleLine(t2) : <span className="font-editorial text-primary">{t2}</span>}
        </h2>

        <p className="mt-12 text-lg md:text-xl leading-relaxed text-cream/75 font-light max-w-2xl mx-auto">
          {subtitle}
        </p>

        {/* Foto opzionale — quando il super_admin la carica dal CMS. */}
        {photo && (
          <div className="mt-16 aspect-[16/9] overflow-hidden rounded-[2rem] max-w-3xl mx-auto">
            <SafeImg src={photo} alt="Il nostro metodo" className="w-full h-full object-cover"/>
          </div>
        )}

        {/* Divider */}
        <div className="mt-20 mb-12 w-16 h-px bg-primary mx-auto" />

        {/* Titolo 'Consapevolezza, cura, trasformazione.' + Bullets — accorpati
            dalla vecchia sezione "Perché Epic Flow". Editabile dal CMS via
            texts.method.bullets_heading_* e texts.method.bullets. */}
        <h3
          data-testid="method-bullets-heading"
          className="font-serif-display font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-cream mb-14"
        >
          {renderBulletsHeading()}
        </h3>

        <ul className="max-w-3xl mx-auto text-left space-y-5">
          {cmsMethodBullets.map((b, i) => (
            <li
              key={`${(b || "").slice(0, 32)}-${i}`}
              data-testid={`method-bullet-${i}`}
              className="flex gap-5 items-start pb-5 border-b border-cream-soft last:border-b-0"
            >
              <div className="mt-1 shrink-0 w-8 h-8 rounded-full bg-primary/25 flex items-center justify-center text-primary">
                <Check className="w-4 h-4" strokeWidth={1.8}/>
              </div>
              <p className="text-base md:text-lg leading-relaxed text-cream/90 font-light">{b}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
